import { clsx } from 'clsx'

import { Avatar } from '@buffer-mono/popcorn'

import styles from './LetterAvatar.module.css'

export const AVATAR_COLORS = [
  {
    color: 'var(--avatar-bg-color-orange)',
    darkColor: 'var(--avatar-text-color-orange)',
  },
  {
    color: 'var(--avatar-bg-color-blue)',
    darkColor: 'var(--avatar-text-color-blue)',
  },
  {
    color: 'var(--avatar-bg-color-pink)',
    darkColor: 'var(--avatar-text-color-pink)',
  },
  {
    color: 'var(--avatar-bg-color-aqua)',
    darkColor: 'var(--avatar-text-color-aqua)',
  },
  {
    color: 'var(--avatar-bg-color-yellow)',
    darkColor: 'var(--avatar-text-color-yellow)',
  },
]

/**
 * Generates a consistent color for a given username using a hash function.
 *
 * This function takes a username and converts it into a hash number, then uses
 * that hash to select a color from a predefined palette. The same username will
 * always get the same color, ensuring visual consistency across the application.
 *
 * @param username - The username to generate a color for
 * @returns An object with background and text color CSS variables from the AVATAR_COLORS palette
 */
export const getAvatarColor = (
  username: string,
): { color: string; darkColor: string } => {
  // Step 1: Convert username to a hash number
  const hash = username.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)

  // Step 2: Use the hash to select a color from the palette
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const isDefaultAvatar = (src: string): boolean => {
  if (!src) return true

  const defaultPatterns = ['Default+Avatar', 'Default']

  return defaultPatterns.some((pattern) => src.includes(pattern))
}

export const LetterAvatar = ({
  src,
  username,
  fallbackLetter,
  size = 'small',
}: {
  src: string
  username: string
  fallbackLetter: string
  size?: 'xsmall' | 'small' | 'mid' | 'medium' | 'large' | 'xlarge'
}): JSX.Element => {
  if (fallbackLetter && isDefaultAvatar(src)) {
    const fallbackColor = getAvatarColor(username)
    const backgroundColor = fallbackColor.color
    const textColor = fallbackColor.darkColor

    const style: React.CSSProperties & { [key: string]: string | number } = {
      '--avatar-size': size === 'small' ? 'var(--size-xl)' : 'var(--size-2xl)',
      backgroundColor: backgroundColor || 'var(--color-bg-neutral)',
      color: textColor || 'var(--color-text-inverted)',
    }

    return (
      <div
        className={clsx(styles.fallbackAvatar, styles[size])}
        style={style}
        role="img"
        aria-label={`Avatar for ${username}`}
      >
        {fallbackLetter}
      </div>
    )
  }

  return <Avatar size={size} src={src} alt={username} />
}
